import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import { parseCharge, formatCharge, parsePgNumericToString } from '@/utils/chargeHandler';
import type { Organization } from '@/config/types/responses';
import type { Pagination } from '@/config/types/pagination';

// eslint-disable-next-line import/prefer-default-export
export const useOrganizationStore = defineStore('organization', {
  state: () => ({
    loading: false,
    error: null as string | null,
    pagination: {} as Pagination,
    organizationResults: [] as Organization[],
    success: null as string | null,
  }),

  actions: {
    async fetchOrganizations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/organizations?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.organizationResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async createOrganization(name: string, contactName: string, contactEmail: string) {
      this.error = null;
      this.success = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/organizations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name, contactName, contactEmail }),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data.error || 'Error creating organization';
          return false;
        }
        this.success = 'Organization created successfully';
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateOrganization(id: string, name: string, contactName: string, contactEmail: string, status: string) {
      this.error = null;
      this.success = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/organizations`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id, name, contactName, contactEmail, status,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data.error || 'Error updating organization';
          return false;
        }
        this.success = 'Organization updated successfully';
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
    async disableOrganization(id: string) {
      this.error = null;
      this.success = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/organizations`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id, status: 'disabled' }),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data.error || 'Error disabling organization';
          return false;
        }
        this.success = 'Organization disabled successfully';
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
    async deleteOrganization(id: string) {
      this.error = null;
      this.success = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/organizations`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id }),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data.error || 'Error deleting organization';
          return false;
        }
        this.success = 'Organization deleted successfully';
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
    clearMessages() {
      this.error = null;
      this.success = null;
    },
  },
});
